/**
 * O «anteninho»: o sinal de wifi de lado que diz como está a ligação, e que ao
 * passar o rato diz QUANTO.
 *
 * Eram três barras verticais e passaram a ser ondas — *"o simbolo deve ser
 * estilo aquele sinal de wifi mesmo, que tava de lado"*. Não é só gosto: as
 * barras de nível são o vocabulário da COBERTURA de rede móvel, e ondas a sair
 * de um ponto são o de uma LIGAÇÃO a acontecer. É a segunda que esta app tem
 * para mostrar, e é a que toda a gente já leu mil vezes numa call.
 *
 * As ondas acendem-se de dentro para fora, e o ponto no vértice está sempre
 * aceso: há ligação — a pergunta é quanta. Perdida apaga as três e risca o
 * conjunto, que é o único estado em que o ícone diz "não" em vez de "quanto".
 *
 * O TÍTULO é onde vive a medida: ping e perda de quem quer que seja aquele nome
 * — o meu medido nesta máquina, o dos outros anunciado pelas deles (ver
 * `netStats.ts`). Sem número, o título é exactamente o que sempre foi.
 */

import { ConnectionQuality } from "livekit-client";

import { useNetStats } from "../hooks/useVoice";
import { type Grau, textoDaLigacao } from "../lib/netStats";

/** O enum do LiveKit traduzido uma vez, aqui, para o resto não depender dele. */
function grauDe(quality: ConnectionQuality): Grau {
  if (quality === ConnectionQuality.Excellent) return "excelente";
  if (quality === ConnectionQuality.Good) return "boa";
  if (quality === ConnectionQuality.Poor) return "fraca";
  if (quality === ConnectionQuality.Lost) return "perdida";
  return "desconhecida";
}

/** Quantas ondas acendem. "Perdida" e "desconhecida" partilham o zero de
 *  propósito: uma diz que não há ligação, a outra que ainda não se sabe, e
 *  nenhuma das duas pode acender uma onda sem mentir. */
const ONDAS: Record<Grau, number> = {
  excelente: 3,
  boa: 2,
  fraca: 1,
  perdida: 0,
  desconhecida: 0,
};

/**
 * As três ondas, do vértice para fora.
 *
 * O centro é o canto inferior esquerdo (3, 21) e cada arco vai do eixo horizontal
 * ao vertical — um quarto de círculo, que é o que dá a inclinação de 45° sem
 * precisar de `transform` nenhum: o desenho JÁ nasce de lado, e assim continua
 * alinhado ao pixel em qualquer tamanho (uma rotação de 45° espalha a linha por
 * dois pixéis e o ícone fica turvo aos 14px em que ele vive).
 */
const RAIOS = [6, 11, 16] as const;
const CENTRO = { x: 3, y: 21 } as const;

function arco(raio: number): string {
  return `M ${CENTRO.x + raio} ${CENTRO.y} A ${raio} ${raio} 0 0 0 ${CENTRO.x} ${CENTRO.y - raio}`;
}

interface Props {
  quality: ConnectionQuality;
  /** De quem é esta ligação (= userId). `null` enquanto a call ainda não tem
   *  identidade — aí há ícone, não há número. */
  identity: string | null;
  size?: number;
}

export function SignalIcon({ quality, identity, size = 15 }: Props) {
  const net = useNetStats();
  const grau = grauDe(quality);
  const acesas = ONDAS[grau];
  // `Date.now()` no render e não num relógio próprio: isto volta a desenhar-se
  // sempre que uma amostra entra, e a minha entra a cada cinco segundos enquanto
  // a chamada durar — portanto a validade é reavaliada nesse mesmo ritmo, sem
  // um segundo temporizador a existir só para envelhecer números.
  const title = textoDaLigacao(grau, identity === null ? null : net[identity], Date.now());

  return (
    // `role="img"`: um <span> com `aria-label` e sem papel nenhum não é
    // anunciado — o nome acessível cai no chão e o leitor de ecrã lê o vazio.
    <span className={`signal signal--${grau}`} role="img" title={title} aria-label={title}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.2}
        strokeLinecap="round"
        aria-hidden="true"
      >
        {RAIOS.map((raio, i) => (
          <path key={raio} d={arco(raio)} className={i < acesas ? "signal__onda" : "signal__onda is-off"} />
        ))}
        <circle cx={CENTRO.x} cy={CENTRO.y} r={1.7} fill="currentColor" stroke="none" />
        {grau === "perdida" ? <path d="M6 6 L20 20" className="signal__corte" /> : null}
      </svg>
    </span>
  );
}
